import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import JSZip from "jszip";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
  RadioGroup,
  Radio,
  FormControlLabel,
  Alert,
  Snackbar,
  Alert as MuiAlert,
} from "@mui/material";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import { db, Product, Sale, Bundle } from "../database/db";
import { ExportProduct } from "./DataExport";

type ImportMode = "clear" | "merge";
type ImportPreview = {
  productCount: number;
  saleCount: number;
  bundleCount: number;
  hasExistingData: boolean;
};

type SnackbarMessage = {
  message: string;
  severity: "success" | "error" | "info" | "warning";
};

export default function DataImport() {
  const [isLoading, setIsLoading] = useState(false);
  const [importMode, setImportMode] = useState<ImportMode>("clear");
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [zipContents, setZipContents] = useState<{
    products: ExportProduct[];
    sales?: Sale[];
    bundles?: Bundle[];
    imageFiles: { [key: string]: Blob };
  } | null>(null);
  const [snackbar, setSnackbar] = useState<SnackbarMessage | null>(null);

  const processZipFile = async (file: File) => {
    setIsLoading(true);
    try {
      const zip = await JSZip.loadAsync(file);
      const imageFiles: { [key: string]: Blob } = {};

      // Read products.json
      const productsJson = await zip.file("products.json")?.async("text");
      const products = productsJson ? JSON.parse(productsJson) : [];

      // Read sales and bundles data if available
      const salesJson = await zip.file("sales.json")?.async("text");
      const bundlesJson = await zip.file("bundles.json")?.async("text");
      const sales = salesJson ? JSON.parse(salesJson) : undefined;
      const bundles = bundlesJson ? JSON.parse(bundlesJson) : undefined;

      // Process image files
      const imagePromises = products
        .filter((p: ExportProduct) => p.imageFilename)
        .map(async (product: ExportProduct) => {
          if (product.imageFilename) {
            const imageFile = zip.file(product.imageFilename);
            if (imageFile) {
              const blob = await imageFile.async("blob");
              imageFiles[product.imageFilename] = blob;
            }
          }
        });

      await Promise.all(imagePromises);

      // Check if there's existing data
      const existingProductCount = await db.products.count();
      const hasExistingData = existingProductCount > 0;

      setPreview({
        productCount: products.length,
        saleCount: sales?.length ?? 0,
        bundleCount: bundles?.length ?? 0,
        hasExistingData,
      });

      setZipContents({
        products,
        sales,
        bundles,
        imageFiles,
      });
    } catch (error) {
      console.error("Error processing zip:", error);
      setSnackbar({
        message:
          "Error processing zip file: " +
          (error instanceof Error ? error.message : "Unknown error"),
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      await processZipFile(file);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/zip": [".zip"],
    },
    maxFiles: 1,
  });

  const ensureUniqueSku = async (
    sku: string,
    attempt: number = 0
  ): Promise<string> => {
    const existingSku = await db.products
      .where("sku")
      .equals(attempt === 0 ? sku : `${sku}-${attempt}`)
      .first();

    if (existingSku) {
      return ensureUniqueSku(sku, attempt + 1);
    }

    return attempt === 0 ? sku : `${sku}-${attempt}`;
  };

  const handleImport = async () => {
    if (!zipContents) return;
    setIsLoading(true);
    let modifiedSkus = 0;

    try {
      // Clear existing data if needed
      if (importMode === "clear") {
        await db.transaction(
          "rw",
          [db.products, db.sales, db.bundles],
          async () => {
            await db.products.clear();
            await db.sales.clear();
            await db.bundles.clear();
          }
        );
      }

      // Import products
      await db.transaction("rw", db.products, async () => {
        for (const product of zipContents.products) {
          const { imageFilename, id: oldId, sku, ...productData } = product;
          const uniqueSku = await ensureUniqueSku(sku);
          if (uniqueSku !== sku) {
            modifiedSkus++;
          }

          const newProduct = { ...productData } as Product;
          if (imageFilename && zipContents.imageFiles[imageFilename]) {
            newProduct.imageBlob = zipContents.imageFiles[imageFilename];
            newProduct.imageMimeType = product.imageMimeType;
          }
          newProduct.sku = uniqueSku;
          await db.products.add(newProduct);
        }
      });

      // Import bundles and sales if available
      if (zipContents.bundles || zipContents.sales) {
        await db.transaction("rw", [db.bundles, db.sales], async () => {
          // Import bundles
          if (zipContents.bundles) {
            for (const bundle of zipContents.bundles) {
              const { id: _, ...bundleData } = bundle;
              await db.bundles.add(bundleData as Bundle);
            }
          }

          // Import sales
          if (zipContents.sales) {
            for (const sale of zipContents.sales) {
              const { id: _, ...saleData } = sale;
              await db.sales.add({
                ...saleData,
                timestamp: new Date(saleData.timestamp),
              } as Sale);
            }
          }
        });
      }

      // Reset state and show success message
      setZipContents(null);
      setPreview(null);
      setImportMode("clear");
      setSnackbar({
        message:
          `Import complete! ${zipContents.products.length} products` +
          (modifiedSkus > 0 ? ` (${modifiedSkus} SKUs modified)` : "") +
          (zipContents.sales ? `, ${zipContents.sales.length} sales` : "") +
          (zipContents.bundles
            ? `, ${zipContents.bundles.length} bundles`
            : "") +
          " imported",
        severity: "success",
      });
    } catch (error) {
      console.error("Import failed:", error);
      setSnackbar({
        message:
          "Import failed: " +
          (error instanceof Error ? error.message : "Unknown error"),
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Import Data
      </Typography>

      <Typography variant="body2" color="text.secondary" paragraph>
        Drop a previously exported ZIP file here to import your data. Make sure
        the file was exported using the export function above.
      </Typography>

      <Box
        {...getRootProps()}
        sx={{
          border: "2px dashed",
          borderColor: isDragActive ? "primary.main" : "grey.300",
          borderRadius: 1,
          p: 3,
          mb: 2,
          textAlign: "center",
          cursor: "pointer",
          "&:hover": {
            borderColor: "primary.main",
            bgcolor: "action.hover",
          },
        }}>
        <input {...getInputProps()} />
        {isLoading ? (
          <CircularProgress size={24} />
        ) : (
          <Typography color="text.secondary">
            {isDragActive
              ? "Drop the ZIP file here"
              : "Drag and drop a ZIP file here, or click to select"}
          </Typography>
        )}
      </Box>

      {preview && (
        <Box sx={{ mt: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Found {preview.productCount} products
            {preview.bundleCount > 0 && ` ${preview.bundleCount} bundles`}
            {preview.saleCount > 0 && ` ${preview.saleCount} sales`} in the
            import file.
          </Alert>

          {preview.hasExistingData && (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                There is existing data in the system. How would you like to
                proceed?
              </Typography>
              <RadioGroup
                value={importMode}
                onChange={(e) => setImportMode(e.target.value as ImportMode)}
                sx={{ mb: 2 }}>
                <FormControlLabel
                  value="clear"
                  control={<Radio />}
                  label="Clear existing data before import"
                />
                <FormControlLabel
                  value="merge"
                  control={<Radio />}
                  label="Merge with existing data"
                />
              </RadioGroup>
            </>
          )}

          <Button
            variant="contained"
            startIcon={
              isLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <FileUploadIcon />
              )
            }
            onClick={handleImport}
            disabled={isLoading}>
            {isLoading ? "Importing..." : "Start Import"}
          </Button>
        </Box>
      )}

      {snackbar && (
        <Snackbar
          open={snackbar !== null}
          autoHideDuration={6000}
          onClose={() => setSnackbar(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
          <MuiAlert
            elevation={6}
            variant="filled"
            severity={snackbar.severity}
            onClose={() => setSnackbar(null)}>
            {snackbar.message}
          </MuiAlert>
        </Snackbar>
      )}
    </Paper>
  );
}
